import { type ApiProblem, type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import { checkoutBlocks, coveringReport, isHardCheckoutBlock } from '@rental/domain'
import { format, formatDateTime, localized } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, Plus, X } from 'lucide-react'
import { type FormEvent, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { canDo } from '../booking-can'
import { bookingQuery } from '../booking-query'
import { DrawerField } from '../cash/DrawerField'
import { ActionRail, RAIL_FORM, RAIL_STEPS } from '../components/ActionRail'
import { StatusBadge } from '../components/BookingItem'
import { ChoiceChips } from '../components/ChoiceChips'
import { MoneyStrip } from '../components/MoneyStrip'
import { OwnerCodeField } from '../components/OwnerCodeField'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { LoadState } from '../components/states'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  StaffInput,
  StaffSelect,
  StaffTextarea,
} from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { secondDriverFacts, secondIdentityId } from '../handover/second-driver'
import { useLocale } from '../i18n'
import {
  MAX_PAPER_PAYMENTS,
  PAPER_CHANNELS,
  PAPER_METHODS,
  type PaperChannel,
  type PaperPaymentRow,
  type PaperWhen,
  paperSet,
} from '../paper/paper-set'
import { problemOf } from '../problems'
import { refreshAfterChange } from '../refresh'
import { FUEL_LEVELS, fuelLabel } from '../rental'

const route = getRouteApi('/bookings/$code/paper')

type Detail = ApiSchemas['StaffBookingDetail']
type Sheet = ApiSchemas['CheckoutSheet']
type Reentry = NonNullable<Detail['reentry']>

/** What a booking in each state may still have on paper. */
const TYPED_IN: readonly Detail['booking']['status'][] = ['confirmed', 'checked_out', 'returned']

/**
 * S11: "Inserimento da carta". A rental kept on paper while the server was
 * down, typed back in by the Owner from its printed set, at the times written
 * on it: the hand-over (with CaRGOS's receipt when one was taken), the return
 * and the money. One request, one Owner's code; every check of the counter is
 * the server's, at the real times (DECISIONS S11).
 */
export function PaperScreen() {
  const { locale, messages: m } = useLocale()
  const { code } = route.useParams()
  const upper = code.toUpperCase()
  const detail = useQuery(bookingQuery(code))
  const booking = detail.data?.booking
  // The hand-over's facts (cars, identity, CaRGOS) only while it can still be typed in.
  const handingOver = booking?.status === 'confirmed'
  const sheet = useQuery({
    queryKey: ['checkout', upper],
    queryFn: () =>
      load(call(api.GET('/v1/staff/bookings/{code}/checkout', { params: { path: { code } } }))),
    enabled: handingOver,
  })
  const problem = problemOf(detail.error) ?? (handingOver ? problemOf(sheet.error) : null)
  const loaded =
    detail.data && (!handingOver || sheet.data)
      ? { detail: detail.data, sheet: handingOver ? (sheet.data ?? null) : null }
      : undefined
  return (
    <>
      <PageHeader
        back={
          <Link
            to="/bookings/$code"
            params={{ code: upper }}
            className="flex min-h-touch items-center gap-1 self-start"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            {m.checkout.back}
          </Link>
        }
        title={format(m.paper.title, { code: upper })}
        status={booking ? <StatusBadge status={booking.status} /> : undefined}
        meta={
          booking
            ? [
                localized(booking.category.name, locale),
                ...(booking.vehicle ? [booking.vehicle.plate] : []),
                `${formatDateTime(locale, booking.startsAt)} → ${formatDateTime(locale, booking.endsAt)}`,
              ].join(' · ')
            : undefined
        }
      />
      <LoadState data={loaded} problem={problem}>
        {(data) => <PaperGate detail={data.detail} sheet={data.sheet} />}
      </LoadState>
    </>
  )
}

/** Said, not offered: someone else, settings unset, or a booking with nothing to type in. */
function PaperGate({ detail, sheet }: { detail: Detail; sheet: Sheet | null }) {
  const { messages: m } = useLocale()
  if (!canDo(detail.can)('reenter')) {
    return (
      <Notice tone="info">
        <p>{m.paper.ownerOnly}</p>
      </Notice>
    )
  }
  if (!detail.reentry) {
    return (
      <Notice tone="warning">
        <p>{m.paper.notConfigured}</p>
      </Notice>
    )
  }
  if (!TYPED_IN.includes(detail.booking.status)) {
    return (
      <Notice tone="info">
        <p>{m.paper.nothing}</p>
      </Notice>
    )
  }
  return <PaperForm detail={detail} sheet={sheet} reentry={detail.reentry} />
}

const blankWhen = (): PaperWhen => ({ date: '', time: '' })

function PaperForm({
  detail,
  sheet,
  reentry,
}: {
  detail: Detail
  sheet: Sheet | null
  reentry: Reentry
}) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { booking, rental } = detail
  const ids = useId()

  const canHandOver = booking.status === 'confirmed' && sheet !== null
  const [withHandover, setWithHandover] = useState(canHandOver)
  const [withReturn, setWithReturn] = useState(booking.status === 'checked_out')
  // A confirmed booking comes back only in a set that also hands it over.
  const returnOffered = booking.status === 'checked_out' || (canHandOver && withHandover)
  const handingOver = canHandOver && withHandover
  const returning = returnOffered && withReturn

  const [sheetNumber, setSheetNumber] = useState('')
  const [vehicleId, setVehicleId] = useState(sheet?.vehicleId ?? '')
  const [handedOver, setHandedOver] = useState(blankWhen)
  const [kmOut, setKmOut] = useState('')
  const [fuelOut, setFuelOut] = useState('')
  const [documentsChecked, setDocumentsChecked] = useState(false)
  const [channel, setChannel] = useState<PaperChannel | ''>('')
  const [receiptNumber, setReceiptNumber] = useState('')
  const [receiptWhen, setReceiptWhen] = useState(blankWhen)
  const [overrideReason, setOverrideReason] = useState('')
  const [returned, setReturned] = useState(blankWhen)
  const [kmIn, setKmIn] = useState('')
  const [fuelIn, setFuelIn] = useState('')
  const [notes, setNotes] = useState('')
  const [payments, setPayments] = useState<PaperPaymentRow[]>([])
  const [drawerId, setDrawerId] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [ownerCode, setOwnerCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const keys = useRef(0)
  const paymentList = useRef<HTMLUListElement>(null)
  const addArea = useRef<HTMLDivElement>(null)

  // The domain's own rules say what the server will ask of this hand-over.
  const identity = sheet?.identity.latest ?? null
  const receiptNeeded =
    sheet !== null &&
    sheet.cargos.state === 'required' &&
    coveringReport(sheet.cargos.transmissions, {
      vehicleId,
      reservationVersion: sheet.version,
      identityId: identity?.id ?? null,
      secondIdentityId: secondIdentityId(sheet),
    }).cover !== 'ok'
  // CaRGOS is typed in the set and the identity is asked for on its own; A8:
  // the kit's numbered contract stands in for the iPad's. What is left the Owner answers for.
  const checks = sheet
    ? checkoutBlocks({
        eligibilityStatus: sheet.eligibility.status,
        identityRecorded: identity !== null,
        birthDateDiffers:
          identity !== null &&
          sheet.identity.bookingBirthDate !== null &&
          sheet.identity.bookingBirthDate !== identity.birthDate,
        cargosRequired: false,
        cargosCover: 'ok',
        contract: 'paper',
        contractRequired: false,
        depositRequired: sheet.deposit.required,
        depositHeld: sheet.deposit.current?.state === 'held',
        secondDriver: secondDriverFacts(sheet),
      })
    : null
  // Q-162: a second driver's identity, like the renter's, is asked for on its own.
  const secondMissing = sheet?.secondIdentity != null && sheet.secondIdentity.latest === null
  const answered = checks ? checks.blocks.filter((block) => !isHardCheckoutBlock(block)) : []
  const overridden = handingOver && answered.length > 0

  const state = paperSet({
    handover: handingOver
      ? {
          sheet: sheetNumber,
          vehicleId,
          ...handedOver,
          km: kmOut,
          fuel: fuelOut,
          documentsChecked,
          identityId: identity?.id ?? null,
          secondIdentityMissing: secondMissing,
          receipt: receiptNeeded ? { channel, number: receiptNumber, ...receiptWhen } : null,
          overridden,
          overrideReason,
        }
      : null,
    return: returning ? { ...returned, km: kmIn, fuel: fuelIn, notes } : null,
    kmOut: rental?.kmOut ?? null,
    payments,
    drawerId,
    reason,
    code: ownerCode,
  })
  const anyCash = payments.some((row) => row.method === 'cash')

  const updatePayment = (key: number, change: Partial<PaperPaymentRow>) =>
    setPayments(payments.map((row) => (row.key === key ? { ...row, ...change } : row)))
  // As at the return's charges: a line added or removed hands the focus on.
  const focusPayment = (index: number) => {
    const rows = paymentList.current?.querySelectorAll<HTMLElement>(':scope > li')
    const row = rows?.[index] ?? rows?.[index - 1]
    const target =
      row?.querySelector<HTMLElement>('input') ?? addArea.current?.querySelector('button')
    target?.focus()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!state.ready || !state.body) return
    setProblem(null)
    // A retry of the same set replays; another code makes another request.
    const serialized = JSON.stringify(state.body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/paper', {
        params: {
          path: { code: booking.code },
          header: { 'idempotency-key': lastSent.current.key },
        },
        body: state.body,
      }),
    )
    // The code is spent once, whatever the answer: a new one is typed.
    setOwnerCode('')
    if (result.ok) {
      setLeaving(true)
      await refreshAfterChange(queryClient, booking.code)
      void navigate({
        to: '/bookings/$code',
        params: { code: booking.code },
        search: { done: 'paper' },
      })
      return
    }
    if (takeOver(result.problem)) {
      setBusy(false)
      return
    }
    setProblem(result.problem)
    // Refused: what the booking is now, read again (another iPad, an online payment).
    if (result.problem.kind === 'api') {
      await queryClient.invalidateQueries({ queryKey: ['booking', booking.code] })
      await queryClient.invalidateQueries({ queryKey: ['checkout', booking.code] })
    }
    setBusy(false)
  }

  const chosen = sheet?.vehicles.find((vehicle) => vehicle.id === vehicleId)
  const leftWith = handingOver ? Number(kmOut) || null : (rental?.kmOut ?? null)

  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} className={RAIL_FORM} noValidate>
      <div className={RAIL_STEPS}>
        <Section title={m.paper.parts}>
          <p>{m.paper.intro}</p>
          <p className="text-sm text-text-muted">
            {format(m.paper.window, { days: reentry.maxDays })}{' '}
            {m.paper.revenueDay[reentry.revenueDay]}
          </p>
          {canHandOver ? (
            <Checkbox
              label={m.paper.partHandover}
              checked={withHandover}
              onChange={(event) => {
                setWithHandover(event.target.checked)
                if (!event.target.checked) setWithReturn(false)
              }}
            />
          ) : null}
          {returnOffered ? (
            <Checkbox
              label={m.paper.partReturn}
              checked={withReturn}
              onChange={(event) => setWithReturn(event.target.checked)}
            />
          ) : null}
        </Section>

        {handingOver && sheet ? (
          <Section title={m.paper.handover}>
            <div className={FIELD_ROW}>
              <Field className={FIELD} label={m.paper.sheet} hint={m.paper.sheetHint} required>
                {(control) => (
                  <StaffInput
                    {...control}
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={20}
                    value={sheetNumber}
                    onChange={(event) => setSheetNumber(event.target.value)}
                  />
                )}
              </Field>
              <Field className={FIELD} label={m.paper.vehicle} required>
                {(control) => (
                  <StaffSelect
                    {...control}
                    value={vehicleId}
                    onChange={(event) => setVehicleId(event.target.value)}
                  >
                    <option value="" />
                    {sheet.vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {`${vehicle.plate} · ${vehicle.make} ${vehicle.model}`}
                        {vehicle.free ? '' : ` (${m.checkout.taken})`}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
            </div>
            <div className={FIELD_ROW}>
              <WhenFields name={`${ids}-handover`} value={handedOver} onChange={setHandedOver} />
            </div>
            <div className={FIELD_ROW}>
              <Field
                className={FIELD}
                label={m.paper.km}
                required
                hint={
                  chosen?.odometerKm != null
                    ? format(m.checkout.kmHint, { km: chosen.odometerKm })
                    : undefined
                }
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={7}
                    value={kmOut}
                    onChange={(event) => setKmOut(event.target.value.replace(/\D/g, ''))}
                  />
                )}
              </Field>
              <FuelField label={m.paper.fuel} value={fuelOut} onChange={setFuelOut} />
            </div>
            <Checkbox
              label={m.paper.documents}
              checked={documentsChecked}
              onChange={(event) => setDocumentsChecked(event.target.checked)}
            />
            {identity ? null : (
              <Notice tone="warning">
                <p>{m.paper.noIdentity}</p>
                <Link
                  to="/bookings/$code/checkout"
                  params={{ code: booking.code }}
                  className="underline"
                >
                  {m.paper.toIdentity}
                </Link>
              </Notice>
            )}
            {secondMissing ? (
              <Notice tone="warning">
                <p>{m.paper.noSecondIdentity}</p>
                <Link
                  to="/bookings/$code/checkout"
                  params={{ code: booking.code }}
                  className="underline"
                >
                  {m.paper.toSecondIdentity}
                </Link>
              </Notice>
            ) : null}
            {sheet.cargos.state === 'required' ? (
              receiptNeeded ? (
                <fieldset className="flex min-w-0 flex-col gap-3">
                  <legend className="text-lg">{m.paper.receipt}</legend>
                  <p className="text-sm text-text-muted">{m.paper.receiptIntro}</p>
                  <ChoiceChips
                    name={`${ids}-channel`}
                    label={m.paper.receiptChannel}
                    options={PAPER_CHANNELS.map((value) => ({
                      value,
                      label: m.paper.receiptChannels[value],
                    }))}
                    value={channel || null}
                    onChange={setChannel}
                  />
                  <div className={FIELD_ROW}>
                    <Field className={FIELD} label={m.paper.receiptNumber} required>
                      {(control) => (
                        <StaffInput
                          {...control}
                          autoComplete="off"
                          maxLength={100}
                          value={receiptNumber}
                          onChange={(event) => setReceiptNumber(event.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className={FIELD_ROW}>
                    <WhenFields
                      name={`${ids}-receipt`}
                      value={receiptWhen}
                      onChange={setReceiptWhen}
                    />
                  </div>
                </fieldset>
              ) : (
                <p className="text-sm">{m.paper.receiptOnFile}</p>
              )
            ) : null}
            {overridden ? (
              <Notice tone="warning">
                <p>
                  {format(m.paper.blocked, {
                    blocks: answered.map((block) => m.checkout.blocks[block]).join('; '),
                  })}
                </p>
                <Field label={m.paper.overrideReason} required>
                  {(control) => (
                    <StaffTextarea
                      {...control}
                      rows={2}
                      maxLength={500}
                      value={overrideReason}
                      onChange={(event) => setOverrideReason(event.target.value)}
                    />
                  )}
                </Field>
              </Notice>
            ) : null}
          </Section>
        ) : null}

        {returning ? (
          <Section title={m.paper.return}>
            <div className={FIELD_ROW}>
              <WhenFields name={`${ids}-return`} value={returned} onChange={setReturned} />
            </div>
            <div className={FIELD_ROW}>
              <Field
                className={FIELD}
                label={m.paper.km}
                required
                hint={leftWith !== null ? format(m.paper.kmHint, { km: leftWith }) : undefined}
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={7}
                    value={kmIn}
                    onChange={(event) => setKmIn(event.target.value.replace(/\D/g, ''))}
                  />
                )}
              </Field>
              <FuelField label={m.paper.fuel} value={fuelIn} onChange={setFuelIn} />
            </div>
            <Field label={m.paper.notes} optionalLabel={m.newBooking.optional}>
              {(control) => (
                <StaffTextarea
                  {...control}
                  rows={3}
                  maxLength={2000}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </Field>
          </Section>
        ) : null}

        <Section title={m.paper.payments}>
          {payments.length === 0 ? (
            <p className="text-text-muted">{m.paper.noPayments}</p>
          ) : (
            <ul ref={paymentList} className="flex flex-col gap-3">
              {payments.map((row, index) => (
                <li key={row.key} className="flex flex-col gap-3 rounded-sm bg-surface-well p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 id={`${ids}-payment-${row.key}`} className="text-lg">
                      {format(m.paper.paymentNumber, { number: index + 1 })}
                    </h3>
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      aria-label={format(m.paper.removePayment, { number: index + 1 })}
                      onClick={() => {
                        flushSync(() =>
                          setPayments(payments.filter((other) => other.key !== row.key)),
                        )
                        focusPayment(index)
                      }}
                    >
                      <X aria-hidden="true" className="size-4" />
                    </Button>
                  </div>
                  <fieldset
                    aria-labelledby={`${ids}-payment-${row.key}`}
                    className="flex min-w-0 flex-col gap-3"
                  >
                    <div className={FIELD_ROW}>
                      <WhenFields
                        name={`${ids}-payment-${row.key}-when`}
                        value={row}
                        onChange={(when) => updatePayment(row.key, when)}
                      />
                    </div>
                    <div className={FIELD_ROW}>
                      <Field className={FIELD} label={m.paper.amount} required>
                        {(control) => (
                          <StaffInput
                            {...control}
                            inputMode="decimal"
                            autoComplete="off"
                            value={row.amount}
                            onChange={(event) =>
                              updatePayment(row.key, { amount: event.target.value })
                            }
                          />
                        )}
                      </Field>
                      <Field
                        className={FIELD}
                        label={m.paper.reference}
                        optionalLabel={m.newBooking.optional}
                      >
                        {(control) => (
                          <StaffInput
                            {...control}
                            autoComplete="off"
                            maxLength={100}
                            value={row.reference}
                            onChange={(event) =>
                              updatePayment(row.key, { reference: event.target.value })
                            }
                          />
                        )}
                      </Field>
                    </div>
                    <ChoiceChips
                      name={`${ids}-payment-${row.key}-method`}
                      label={m.paper.method}
                      options={PAPER_METHODS.map((value) => ({
                        value,
                        label: m.paymentMethods[value],
                      }))}
                      value={row.method || null}
                      onChange={(method) => updatePayment(row.key, { method })}
                    />
                  </fieldset>
                </li>
              ))}
            </ul>
          )}
          {anyCash ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-text-muted">{m.paper.drawerHint}</p>
              <DrawerField name={`${ids}-drawer`} onChange={setDrawerId} />
            </div>
          ) : null}
          {payments.length < MAX_PAPER_PAYMENTS ? (
            <div ref={addArea}>
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => {
                  keys.current += 1
                  const key = keys.current
                  flushSync(() =>
                    setPayments([
                      ...payments,
                      { key, date: '', time: '', amount: '', method: '', reference: '' },
                    ]),
                  )
                  focusPayment(payments.length)
                }}
              >
                <Plus aria-hidden="true" className="size-4" />
                {m.paper.addPayment}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-text-muted">
              {format(m.paper.paymentsFull, { max: MAX_PAPER_PAYMENTS })}
            </p>
          )}
        </Section>

        <Section title={m.paper.owner}>
          <Field label={m.paper.reason} hint={m.paper.reasonHint} required>
            {(control) => (
              <StaffTextarea
                {...control}
                rows={2}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
          <OwnerCodeField
            className={FIELD}
            label={m.paper.code}
            value={ownerCode}
            onChange={setOwnerCode}
          />
        </Section>
      </div>

      <ActionRail
        title={m.paper.railTitle}
        action={m.paper.submit}
        detailsLabel={m.paper.railDetails}
        ready={state.ready}
        busy={busy || leaving}
        reasons={state.reasons.map((why) => m.paper.why[why])}
        problem={problem}
      >
        <div className="flex flex-col gap-2">
          <MoneyStrip money={detail.money} />
          <p className="text-sm text-text-muted">{m.paper.moneyHint}</p>
        </div>
      </ActionRail>
    </form>
  )
}

/** A day and an hour as written on the sheet, side by side where they fit. */
function WhenFields({
  name,
  value,
  onChange,
}: {
  name: string
  value: PaperWhen
  onChange: (value: PaperWhen) => void
}) {
  const { messages: m } = useLocale()
  return (
    <FieldPair>
      <Field className={PAIRED_FIELD} label={m.paper.date} required>
        {(control) => (
          <StaffInput
            {...control}
            name={`${name}-date`}
            type="date"
            value={value.date}
            onChange={(event) => onChange({ date: event.target.value, time: value.time })}
          />
        )}
      </Field>
      <Field className={PAIRED_FIELD} label={m.paper.time} required>
        {(control) => (
          <StaffInput
            {...control}
            name={`${name}-time`}
            type="time"
            value={value.time}
            onChange={(event) => onChange({ date: value.date, time: event.target.value })}
          />
        )}
      </Field>
    </FieldPair>
  )
}

function FuelField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const { messages: m } = useLocale()
  return (
    <Field className={FIELD} label={label} required>
      {(control) => (
        <StaffSelect {...control} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="" />
          {FUEL_LEVELS.map((level) => (
            <option key={level} value={level}>
              {fuelLabel(level, m)}
            </option>
          ))}
        </StaffSelect>
      )}
    </Field>
  )
}
